import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateEvent = () => {
  const navigate = useNavigate();
  const { token, agent } = useAuth();
  const dashboardPath = agent?.role === 'broker' ? '/broker' : '/dashboard';
  const [formData, setFormData] = useState({
    property_address: '',
    date: '',
    time: '13:00',
    end_time: '16:00',
    description: '',
    property_type: '',
    template_id: ''
  });
  const [templates, setTemplates] = useState([]);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchTemplates();
    fetchPropertyTypes();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates');
    }
  };

  const fetchPropertyTypes = async () => {
    try {
      const response = await axios.get(`${API}/property-types`);
      setPropertyTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch property types');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/events`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event created successfully!');
      navigate(`/events/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(dashboardPath)}
          className="mb-6 text-stone-600 hover:text-[#1A4D2E]"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="mr-2" size={20} />
          Back to Dashboard
        </Button>
        
        <Card className="bg-white p-4 md:p-6 rounded-2xl card-shadow">
          <h1 className="text-2xl md:text-3xl font-bold text-[#1A4D2E] mb-1" style={{fontFamily: '"Playfair Display", serif'}}>Create Open House</h1>
          <p className="text-stone-600 text-sm mb-4">Set up a new event and start capturing leads</p>
          
          <form onSubmit={handleSubmit} className="space-y-3 md:space-y-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1 block">Property Address</Label>
              <Input
                data-testid="create-event-address"
                type="text"
                name="property_address"
                value={formData.property_address}
                onChange={handleChange}
                className="h-10 md:h-11 bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] focus:ring-0 rounded-lg px-3"
                placeholder="123 Main Street, City, State"
                required
              />
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1 block">Date</Label>
                <Input
                  data-testid="create-event-date"
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  className="h-10 md:h-11 bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] focus:ring-0 rounded-lg px-3 text-sm"
                  required
                />
              </div>
              
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1 block">Start Time</Label>
                <Input
                  data-testid="create-event-time"
                  type="time"
                  name="time"
                  value={formData.time}
                  onChange={handleChange}
                  className="h-10 md:h-11 bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] focus:ring-0 rounded-lg px-3 text-sm"
                  required
                />
              </div>
            </div>
            
            <div>
              <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block">End Time</Label>
              <Input
                data-testid="create-event-end-time"
                type="time"
                name="end_time"
                value={formData.end_time}
                onChange={handleChange}
                className="h-14 bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] focus:ring-0 rounded-lg text-lg px-4"
                required
              />
            </div>
            
            <div>
              <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block">Property Type</Label>
              <Select
                value={formData.property_type}
                onValueChange={(value) => setFormData({...formData, property_type: value})}
              >
                <SelectTrigger data-testid="create-event-property-type" className="h-14 bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] focus:ring-0 rounded-lg text-lg px-4">
                  <SelectValue placeholder="Select property type" />
                </SelectTrigger>
                <SelectContent>
                  {propertyTypes.map(pt => (
                    <SelectItem key={pt} value={pt}>{pt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block">Description (Optional)</Label>
              <Textarea
                data-testid="create-event-description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="min-h-32 bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] focus:ring-0 rounded-lg text-lg px-4 py-3"
                placeholder="Property features, parking instructions, etc."
              />
            </div>
            
            <div>
              <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block">Sign-In Template (Optional)</Label>
              <select
                data-testid="create-event-template"
                name="template_id"
                value={formData.template_id}
                onChange={handleChange}
                className="h-14 w-full bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] focus:ring-0 rounded-lg text-lg px-4"
              >
                <option value="">Default Template</option>
                {templates.map(template => (
                  <option key={template.id} value={template.id}>{template.name}</option>
                ))}
              </select>
              <p className="text-xs text-stone-500 mt-2">Choose a custom template or use the default sign-in form</p>
            </div>
            
            <div className="flex gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(dashboardPath)}
                className="flex-1 border-2 border-[#1A4D2E] text-[#1A4D2E] bg-transparent rounded-full h-14 text-lg hover:bg-[#1A4D2E]/5"
              >
                Cancel
              </Button>
              <Button
                data-testid="submit-create-event"
                type="submit"
                disabled={loading}
                className="flex-1 bg-[#1A4D2E] text-white rounded-full h-14 text-lg hover:bg-[#143D24] transition-transform hover:scale-105 active:scale-95 shadow-lg shadow-green-900/20"
              >
                {loading ? 'Creating...' : 'Create Event'}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </Layout>
  );
};

export default CreateEvent;
